import type { GridColDef } from '@mui/x-data-grid';

import useSWR from 'swr';
import { toast } from 'sonner';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

import axiosInstance, { fetcher } from 'src/lib/axios';
import { anexarProtocolo } from 'src/actions/anexos';
import { DashboardContent } from 'src/layouts/dashboard/content';
import { TxDataGridClient } from 'src/auditoria/components/tx-data-grid/TxDataGridClient';
import {
  DsNum,
  DsField,
  DsButton,
  colunaLink,
  GradeCanvas,
  DsPageHeader,
  DsStatusChip,
  SX_GRADE_CANVAS,
} from 'src/auditoria/components/ds';

import { formatDateBr } from '../Nfse/types';
import { SX_COLUNAS, AnexosCanvas, toneSituacao, PainelConversa, TituloConversa } from './canvas';

// ----------------------------------------------------------------------
// Protocolo → Meus Processos (artboard `portal-protocolo`). Backend:
// GET /portal-api/protocolo (protocolo.cadastro_processo do banco fiscal,
// filtrado por fkpersona). Ver SPEC-PORTAL-SN-TOMADOR-PF.md §6.
//
// Três blocos do canvas: grade, painel de thread do processo selecionado e o
// painel de 300px de abertura de protocolo. O pager FICA (decisão do dono do
// produto: a lista pode crescer).
// ----------------------------------------------------------------------

interface Processo {
  protocolo: string;
  tipo: string | null;
  assunto: string | null;
  status: string | null;
  prazo: string | null;
  data: string | null;
  solicitante: string | null;
  descricao: string | null;
}

interface ProtocoloResposta {
  processos: Processo[];
}

interface MensagemProcesso {
  id: number;
  mensagem: string | null;
  data_hora: string | null;
  remetente: 'A' | 'C' | null;
}

interface ProcessoDetalhe {
  processo: { protocolo: string; assunto: string | null } | null;
  mensagens: MensagemProcesso[];
}

interface Assunto {
  id: number;
  nome: string;
  descricao: string | null;
  tipo: string | null;
}

interface MeusDados {
  email?: string | null;
  telefone?: string | null;
  cep?: string | null;
}

export default function ProtocoloListPage() {
  const navigate = useNavigate();
  const { data, isLoading, mutate } = useSWR<ProtocoloResposta>('/portal-api/protocolo', fetcher);
  const processos = data?.processos ?? [];

  // --- thread do processo selecionado (o segundo card do artboard) ---------
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const aberto = selecionado ?? processos[0]?.protocolo ?? null;

  const { data: detalhe, mutate: recarregarThread } = useSWR<ProcessoDetalhe>(
    aberto ? `/portal-api/protocolo/detalhe?protocolo=${encodeURIComponent(aberto)}` : null,
    fetcher
  );

  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviandoMensagem, setEnviandoMensagem] = useState(false);

  const enviarMensagem = async () => {
    const texto = novaMensagem.trim();
    if (!texto || !aberto) return;
    setEnviandoMensagem(true);
    try {
      await axiosInstance.post('/portal-api/protocolo/mensagens', {
        protocolo: aberto,
        mensagem: texto,
      });
      setNovaMensagem('');
      await recarregarThread();
    } catch {
      toast.error('Não foi possível enviar a mensagem.');
    } finally {
      setEnviandoMensagem(false);
    }
  };

  // --- painel de abertura (o rail de 300px) -------------------------------
  const { data: assuntosData } = useSWR<{ assuntos: Assunto[] }>(
    '/portal-api/protocolo/assuntos',
    fetcher
  );
  const assuntos = assuntosData?.assuntos ?? [];
  const { data: meusDados } = useSWR<MeusDados>('/portal-api/protocolo/meus-dados', fetcher);

  const [caId, setCaId] = useState<number | ''>('');
  const [descricao, setDescricao] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cep, setCep] = useState('');
  const [anexos, setAnexos] = useState<File[]>([]);
  const [enviando, setEnviando] = useState(false);

  // O cadastro do contribuinte preenche contato/CEP; o campo vazio cai no valor
  // do cadastro sem sobrescrever o que já foi digitado.
  const valorEmail = email || (meusDados?.email ?? '');
  const valorTelefone = telefone || (meusDados?.telefone ?? '');
  const valorCep = cep || (meusDados?.cep ?? '');

  const trocarAssunto = (novoId: number | '') => {
    setCaId(novoId);
    const alvo = assuntos.find((a) => a.id === novoId);
    if (alvo?.descricao) setDescricao((v) => v || alvo.descricao!);
  };

  const cadastrar = async () => {
    if (caId === '') {
      toast.error('Selecione o assunto.');
      return;
    }
    setEnviando(true);
    try {
      const res = await axiosInstance.post('/portal-api/protocolo', {
        ca_id: caId,
        cp_descricao: descricao || undefined,
        cp_email: valorEmail || undefined,
        cp_telefone: valorTelefone || undefined,
        cp_cep: valorCep || undefined,
      });
      const protocolo: string = res.data?.protocolo ?? '';
      toast.success(`Processo aberto: ${protocolo}`);

      if (protocolo && anexos.length) {
        const falhas = await anexarProtocolo(protocolo, anexos);
        if (falhas > 0)
          toast.error(`${falhas} anexo(s) não puderam ser enviados. Reenvie no processo.`);
      }

      setCaId('');
      setDescricao('');
      setAnexos([]);
      await mutate();
      if (protocolo) setSelecionado(protocolo);
    } catch {
      toast.error('Falha ao abrir o processo.');
    } finally {
      setEnviando(false);
    }
  };

  // Colunas do canvas: Número / Assunto / Abertura / Situação, em 130 / 1fr /
  // 110 / 130. Tipo e Prazo vivem no detalhe do processo, fora da grade.
  const colunas: GridColDef<Processo>[] = [
    colunaLink({ field: 'protocolo', headerName: 'Número', width: 130 }, (row) =>
      setSelecionado(row.protocolo)
    ),
    { field: 'assunto', headerName: 'Assunto', flex: 1, minWidth: 220 },
    {
      field: 'data',
      headerName: 'Abertura',
      width: 110,
      renderCell: (p) => <DsNum mono>{formatDateBr(p.row.data)}</DsNum>,
    },
    {
      field: 'status',
      headerName: 'Situação',
      width: 130,
      renderCell: (p) =>
        p.row.status ? (
          <DsStatusChip tone={toneSituacao(p.row.status)}>{p.row.status}</DsStatusChip>
        ) : (
          <Box component="span">—</Box>
        ),
    },
  ];

  const processoAberto = processos.find((p) => p.protocolo === aberto);

  return (
    <DashboardContent>
      <DsPageHeader
        weight={800}
        title="Meus Processos"
        subtitle="Abertura e acompanhamento de processos · mensagens com o fiscal."
        action={
          <DsButton
            tone="primary"
            size="tall"
            icon="mais"
            onClick={() => navigate('/protocolo/novo')}
          >
            Abrir protocolo
          </DsButton>
        }
      />

      <Box sx={SX_COLUNAS}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <GradeCanvas>
            <TxDataGridClient<Processo>
              rows={processos}
              columns={colunas}
              getRowId={(row) => row.protocolo}
              autoHeight
              hideToolbar
              hideFooterToolbar
              hidePageSizeSelector
              initialPageSize={10}
              localeText={{
                noRowsLabel: isLoading ? 'Carregando processos…' : 'Nenhum processo encontrado.',
              }}
              {...SX_GRADE_CANVAS}
            />
          </GradeCanvas>

          {aberto && (
            <PainelConversa
              titulo={
                <TituloConversa
                  numero={aberto}
                  assunto={processoAberto?.assunto ?? detalhe?.processo?.assunto ?? null}
                  aoAbrir={() => navigate(`/protocolo/${encodeURIComponent(aberto)}`)}
                />
              }
              mensagens={(detalhe?.mensagens ?? []).map((m) => ({
                id: m.id,
                texto: m.mensagem ?? '',
                doContribuinte: m.remetente === 'C',
              }))}
              vazio="Ainda não há mensagens neste processo."
              placeholder="Escreva uma mensagem ao fiscal…"
              valor={novaMensagem}
              onChange={setNovaMensagem}
              onEnviar={enviarMensagem}
              enviando={enviandoMensagem}
            />
          )}
        </Box>

        <Card sx={{ flex: '0 0 300px', padding: '18px 20px' }}>
          <Box sx={{ fontSize: 13, fontWeight: 800, mb: '10px' }}>Abrir protocolo</Box>

          <DsField label="Assunto" density="sm" sx={{ mb: '10px' }}>
            <TextField
              select
              fullWidth
              value={caId}
              onChange={(e) => trocarAssunto(e.target.value === '' ? '' : Number(e.target.value))}
            >
              {assuntos.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  {a.nome}
                  {a.tipo ? ` · ${a.tipo}` : ''}
                </MenuItem>
              ))}
            </TextField>
          </DsField>

          <DsField label="Descrição / solicitação" density="sm" sx={{ mb: '10px' }}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva o que você precisa…"
            />
          </DsField>

          <DsField label="E-mail" density="sm" sx={{ mb: '10px' }}>
            <TextField fullWidth value={valorEmail} onChange={(e) => setEmail(e.target.value)} />
          </DsField>

          <DsField label="Telefone" density="sm" sx={{ mb: '10px' }}>
            <TextField
              fullWidth
              value={valorTelefone}
              onChange={(e) => setTelefone(e.target.value)}
            />
          </DsField>

          <DsField label="CEP" density="sm" sx={{ mb: '10px' }}>
            <TextField fullWidth value={valorCep} onChange={(e) => setCep(e.target.value)} />
          </DsField>

          <DsField label="Anexos" density="sm" sx={{ mb: '10px' }}>
            <AnexosCanvas arquivos={anexos} onChange={setAnexos} disabled={enviando} />
          </DsField>

          <DsButton
            tone="primary"
            size="tall"
            disabled={enviando}
            onClick={cadastrar}
            sx={{ width: '100%', justifyContent: 'center' }}
          >
            Cadastrar Processo
          </DsButton>
        </Card>
      </Box>
    </DashboardContent>
  );
}
